import type { Task } from "../tasks/types.js";
import type { BackupEnvelope } from "./types.js";

/** Which categories of a backup the user chose to import. */
export interface BackupSelection {
  /** Non-event tasks. */
  tasks: boolean;
  /** Events (is_event === true). */
  events: boolean;
  labels: boolean;
  topics: boolean;
  calendars: boolean;
  /** The curated, non-secret preferences block. */
  settings: boolean;
}

/** Row ids to hard-delete for a selective "Replace" — see {@link planSelectiveClear}. */
export interface ClearIds {
  taskIds: string[];
  ruleIds: string[];
  overrideIds: string[];
  labelIds: string[];
  topicIds: string[];
  calendarIds: string[];
}

/**
 * Given the CURRENT data graph and the categories the user chose to replace,
 * compute exactly which rows to delete. Tasks/events split on `is_event`; a
 * removed series' rules + overrides go with it. Unchecked categories yield no
 * deletions — so "Replace, Settings only" deletes nothing. Pure.
 */
export function planSelectiveClear(cur: BackupEnvelope, sel: BackupSelection): ClearIds {
  const removeTask = (t: Task): boolean => (t.is_event ? sel.events : sel.tasks);
  const tasks = cur.tasks.filter(removeTask);
  const ruleIdSet = new Set(tasks.map((t) => t.recurrence_id).filter(Boolean) as string[]);
  return {
    taskIds: tasks.map((t) => t.id),
    ruleIds: cur.recurrenceRules.filter((r) => ruleIdSet.has(r.id)).map((r) => r.id),
    overrideIds: cur.recurrenceOverrides.filter((o) => ruleIdSet.has(o.recurrence_id)).map((o) => o.id),
    labelIds: sel.labels ? cur.labels.map((l) => l.id) : [],
    topicIds: sel.topics ? (cur.topics ?? []).map((t) => t.id) : [],
    calendarIds: sel.calendars ? (cur.calendars ?? []).map((c) => c.id) : [],
  };
}

/** Per-category counts shown next to the import checkboxes. */
export interface BackupSummary {
  tasks: number;
  events: number;
  labels: number;
  topics: number;
  calendars: number;
  hasSettings: boolean;
}

export function summarizeEnvelope(env: BackupEnvelope): BackupSummary {
  let tasks = 0;
  let events = 0;
  for (const t of env.tasks) t.is_event ? events++ : tasks++;
  return {
    tasks,
    events,
    labels: env.labels.length,
    topics: (env.topics ?? []).length,
    calendars: (env.calendars ?? []).length,
    hasSettings: !!env.preferences || (env.themes ?? []).length > 0,
  };
}

/**
 * Narrow an envelope to the chosen categories. Recurrence rules/overrides ride
 * with the tasks/events that reference them (a rule is kept only if a kept task
 * points at it; an override only if its rule survives). Entity references on
 * kept tasks (label/topic/calendar ids) are left intact — they resolve if the
 * entity already exists locally or is imported, and degrade gracefully otherwise.
 * Pure — no I/O.
 */
export function selectFromEnvelope(env: BackupEnvelope, sel: BackupSelection): BackupEnvelope {
  const keepTask = (t: Task): boolean => (t.is_event ? sel.events : sel.tasks);
  const tasks = env.tasks.filter(keepTask);

  const keptRuleIds = new Set(tasks.map((t) => t.recurrence_id).filter(Boolean) as string[]);
  const recurrenceRules = env.recurrenceRules.filter((r) => keptRuleIds.has(r.id));
  const ruleIds = new Set(recurrenceRules.map((r) => r.id));
  const recurrenceOverrides = env.recurrenceOverrides.filter((o) => ruleIds.has(o.recurrence_id));

  return {
    version: env.version,
    exportedAt: env.exportedAt,
    tasks,
    recurrenceRules,
    recurrenceOverrides,
    labels: sel.labels ? env.labels : [],
    topics: sel.topics ? (env.topics ?? []) : [],
    calendars: sel.calendars ? (env.calendars ?? []) : [],
    // Omit the key entirely when settings aren't selected (exactOptionalPropertyTypes).
    ...(sel.settings && env.preferences ? { preferences: env.preferences } : {}),
    ...(sel.settings && env.themes ? { themes: env.themes } : {}),
  };
}
